import type { ValidationResult } from '@aidkitorg/types/lib/distroValidator/distroValidator';
import { parseConfigPath, type ConfigEditOp } from './configEdit';

export const OMITTED_TRANSLATION_MARKER = '[omitted]';

const CONTAINER_KEYS = new Set([
  'components', 'subsurveys', 'notifications', 'sections',
  'survey', 'dashboards', 'payments', 'personas', 'exports',
  'fraudFlags', 'crossProgramDataExchange', 'sync',
]);

const MULTILINGUAL_FIELDS = new Set([
  'name', 'title', 'content', 'label', 'placeholder', 'description', 'text', 'body', 'subject', 'heading',
]);

/**
 * Char budget for one searchConfig result, measured on the whole returned object.
 * Deliberately well under the editor's MAX_TOOL_RESULT_CHARS (50k): boundToolResult's
 * read branch replaces an oversized payload outright, so a search must never reach it.
 */
export const SEARCH_RESULT_BUDGET = 20_000;
/** Slack for the envelope: counts, notes, and the `document` meta the caller prepends. */
const SEARCH_ENVELOPE_RESERVE = 1_500;

export const MAX_OBJECT_CHARS = 50_000;

function toEnglish(value: unknown): string | undefined {
  if (typeof value === 'string') return value.slice(0, 80);
  if (value && typeof value === 'object' && typeof (value as { en?: unknown }).en === 'string') {
    return (value as { en: string }).en.slice(0, 80);
  }
  return undefined;
}

function summarizeNode(value: any, depth = 0): any {
  if (Array.isArray(value)) {
    const summarized = value.map(item => summarizeNode(item, depth + 1));
    return summarized.length > 0 && summarized.every(item => item === '...')
      ? { count: value.length }
      : summarized;
  }
  if (!value || typeof value !== 'object') return value;
  if (depth > 5) return '...';

  const summary: Record<string, unknown> = {};
  if (value.kind) summary.kind = value.kind;
  if (value.targetField) summary.targetField = value.targetField;
  if (value.path) summary.path = value.path;
  const name = toEnglish(value.name);
  if (name) summary.name = name;
  const title = toEnglish(value.title);
  if (title) summary.title = title;
  if (value.condition) summary.hasCondition = true;

  for (const key of Object.keys(value)) {
    if (CONTAINER_KEYS.has(key) && value[key] && typeof value[key] === 'object') {
      summary[key] = summarizeNode(value[key], depth + 1);
    }
  }
  return summary;
}

export function summarizeConfig(config: unknown): Record<string, unknown> {
  if (!config) return { error: 'No survey loaded' };
  const expanded = !Array.isArray(config) && typeof config === 'object' && 'survey' in config;
  return {
    format: expanded ? 'ExpandedSurvey' : 'Survey (flat array)',
    summary: summarizeNode(config),
  };
}

/** A match, kept unrendered so the detail level can be chosen after the whole walk. */
type SearchHit = {
  path: string,
  kind?: string,
  targetField?: string,
  name?: string,
  title?: string,
  matches: { key: string, value: string }[],
};

function renderFullHit(hit: SearchHit): Record<string, unknown> {
  return {
    path: hit.path,
    kind: hit.kind,
    ...(hit.targetField && { targetField: hit.targetField }),
    ...(hit.name && { name: hit.name }),
    ...(hit.title && { title: hit.title }),
    matches: hit.matches.map(m => `${m.key}: ${m.value}`),
  };
}

/**
 * Compact form, used when full detail won't fit. Drops name/title and shortens the
 * snippets, but keeps kind and targetField — both short, and usually enough to tell
 * whether a hit needs editing — and the matched KEY names, which say why the path
 * matched at all (condition vs text vs expr).
 */
function renderPathHit(hit: SearchHit): Record<string, unknown> {
  return {
    path: hit.path,
    kind: hit.kind,
    ...(hit.targetField && { targetField: hit.targetField }),
    matches: hit.matches.map(m => `${m.key}: ${m.value.slice(0, 40)}`),
  };
}

/**
 * Search a config for components matching a pattern.
 *
 * Fills a char budget and reports truncated/nextOffset instead of erroring when
 * results are large. Returning only a count — what this used to do past 10k chars —
 * left an agent asked to update every reference to a field with nothing to act on
 * and no way to reach the rest.
 */
export function searchConfig(
  config: unknown,
  pattern: string,
  field?: string,
  offset: number = 0,
): Record<string, unknown> {
  if (!config) return { error: 'No survey loaded' };
  if (!Number.isInteger(offset) || offset < 0) {
    return {
      error: `Invalid offset ${JSON.stringify(offset)}. "offset" must be a whole number of 0 or more — `
        + 'pass the "nextOffset" from the previous result.',
    };
  }

  let regex: RegExp;
  try {
    regex = new RegExp(pattern, 'i');
  } catch (error) {
    return {
      error: `Invalid regex pattern "${pattern}": ${error instanceof Error ? error.message : String(error)}. `
        + 'Use a valid regex or a literal string.',
    };
  }

  const kindMatches: SearchHit[] = [];
  const contentMatches: SearchHit[] = [];
  const visit = (value: any, path: string[] = []) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, [...path, `[${index}]`]));
      return;
    }
    if (!value || typeof value !== 'object') return;

    const matches: SearchHit['matches'] = [];
    const kindMatch = typeof value.kind === 'string' && regex.test(value.kind);
    if (field) {
      const candidate = value[field];
      if (typeof candidate === 'string' && regex.test(candidate)) {
        matches.push({ key: field, value: candidate.slice(0, 80) });
      }
    } else {
      for (const [key, candidate] of Object.entries(value)) {
        if (typeof candidate === 'string' && regex.test(candidate)) {
          matches.push({ key, value: candidate.slice(0, 80) });
        }
      }
    }

    if (matches.length > 0) {
      const hit: SearchHit = {
        path: path.join('.'),
        kind: value.kind,
        ...(value.targetField && { targetField: value.targetField }),
        ...(value.name && { name: toEnglish(value.name) }),
        ...(value.title && { title: toEnglish(value.title) }),
        matches,
      };
      (kindMatch ? kindMatches : contentMatches).push(hit);
    }

    for (const [key, child] of Object.entries(value)) {
      if (child && typeof child === 'object') visit(child, [...path, key]);
    }
  };
  visit(config);

  // Prioritize kind matches, then content matches
  const ordered = [...kindMatches, ...contentMatches];
  const budget = SEARCH_RESULT_BUDGET - SEARCH_ENVELOPE_RESERVE;

  // Detail level is chosen ONCE over the whole result set, not per page: this
  // function is stateless and re-walks the document every call, so deciding per
  // page would flip the entry shape partway through an enumeration.
  const fitsFull = JSON.stringify(ordered.map(renderFullHit)).length <= budget;
  const render = fitsFull ? renderFullHit : renderPathHit;

  const results: Record<string, unknown>[] = [];
  let used = 0;
  let i = offset;
  for (; i < ordered.length; i++) {
    const entry = render(ordered[i]);
    const cost = JSON.stringify(entry).length + 1;
    // Always take at least one entry, or a single oversized match returns an empty
    // page whose nextOffset equals offset and the agent pages forever.
    if (results.length > 0 && used + cost > budget) break;
    results.push(entry);
    used += cost;
  }
  const truncated = i < ordered.length;

  const notes: string[] = [];
  if (!fitsFull) {
    notes.push('Results are abbreviated (name/title dropped, snippets shortened) because full detail did not '
      + 'fit. Get one path in full with the object read tool.');
  }
  if (truncated) {
    notes.push(`Showing matches ${offset}-${i - 1} of ${ordered.length}. Call again with offset: ${i} for the `
      + 'next page, and keep going until truncated is false.');
  } else if (offset >= ordered.length && ordered.length > 0) {
    notes.push(`Offset ${offset} is past the end — there are ${ordered.length} matches in total, and you have `
      + 'seen them all.');
  }

  return {
    pattern,
    ...(field && { field }),
    totalCount: ordered.length,
    kindMatchCount: kindMatches.length,
    offset,
    returnedCount: results.length,
    detail: fitsFull ? 'full' : 'paths',
    results,
    truncated,
    ...(truncated && { nextOffset: i }),
    ...(notes.length > 0 && { note: notes.join(' ') }),
  };
}

export function stripConfigForAgent(
  value: any,
  options: { showAllLangs?: boolean } = {},
  depth = 0,
  maxDepth = 3,
): any {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    if (depth >= maxDepth) {
      return value.map((item, index) => item && typeof item === 'object'
        ? {
          _index: index,
          kind: item.kind,
          ...(item.targetField && { targetField: item.targetField }),
          ...(item.name && { name: toEnglish(item.name) }),
        }
        : item);
    }
    return value.map(item => stripConfigForAgent(item, options, depth + 1, maxDepth));
  }
  if (typeof value !== 'object') return value;

  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (!options.showAllLangs && MULTILINGUAL_FIELDS.has(key) && child && typeof child === 'object'
      && !Array.isArray(child) && 'en' in child) {
      result[key] = Object.fromEntries(Object.entries(child).map(([language, translation]) => [
        language,
        language === 'en' ? translation : OMITTED_TRANSLATION_MARKER,
      ]));
    } else {
      result[key] = stripConfigForAgent(child, options, depth, maxDepth);
    }
  }
  return result;
}

export function getConfigObject(config: unknown, path: string, showAllLangs = false): Record<string, unknown> {
  if (!config) return { error: 'No survey loaded' };
  try {
    const segments = parseConfigPath(path);
    if (!segments) {
      return { error: `Could not parse path "${path}". Use property and index access only, e.g. "survey[0].components[1]".` };
    }
    let current: any = config;
    for (const segment of segments) {
      current = segment.kind === 'prop' ? current?.[segment.prop] : current?.[segment.index];
      if (current === undefined || current === null) return { error: `Path not found: ${path}` };
    }

    const content = stripConfigForAgent(current, { showAllLangs });
    if (JSON.stringify(content).length > MAX_OBJECT_CHARS) {
      return {
        path,
        warning: 'Object is large, showing summary only. Use a more specific path to drill deeper.',
        keys: Object.keys(current),
        kind: current.kind,
        childCount: Array.isArray(current) ? current.length : undefined,
      };
    }
    return { path, content };
  } catch (error) {
    return { error: `Error accessing path: ${error}` };
  }
}

const VALID_EDIT_OPS = new Set(['replace', 'insert', 'delete', 'test']);

export function jsonParseFailureDetail(edits: string, message: string): string {
  const position = /position (\d+)/.exec(message);
  const context = position
    ? ` The text around that position is: …${JSON.stringify(edits.slice(Math.max(0, Number(position[1]) - 80), Number(position[1]) + 40))}…`
    : '';
  return `"edits" is not valid JSON: ${message}.${context} Re-emit the edits array; do not retry the identical string.`;
}

function omittedMarkerPaths(value: unknown): string[] {
  const found: string[] = [];
  const stack: Array<{ value: unknown; path: string }> = [{ value, path: 'edits' }];
  let budget = 200_000;
  while (stack.length > 0 && found.length < 10 && budget-- > 0) {
    const current = stack.pop()!;
    if (typeof current.value === 'string') {
      if (current.value === OMITTED_TRANSLATION_MARKER) found.push(current.path);
    } else if (Array.isArray(current.value)) {
      current.value.forEach((item, index) => stack.push({ value: item, path: `${current.path}[${index}]` }));
    } else if (current.value && typeof current.value === 'object') {
      Object.entries(current.value).forEach(([key, child]) => stack.push({ value: child, path: `${current.path}.${key}` }));
    }
  }
  return found;
}

export function parseConfigEditOps(
  edits: unknown,
  opts?: { readToolName?: string },
): { ops: ConfigEditOp[] } | { error: string } {
  if (typeof edits !== 'string' || edits.trim() === '') {
    return { error: '"edits" is required: a JSON string containing an array of edit operations.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(edits);
  } catch (error) {
    return { error: jsonParseFailureDetail(edits, error instanceof Error ? error.message : String(error)) };
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return { error: '"edits" must be a non-empty ARRAY of operations, e.g. [{"op":"replace","path":"...","value":...}].' };
  }

  const ops: ConfigEditOp[] = [];
  for (let index = 0; index < parsed.length; index++) {
    const raw = parsed[index];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return { error: `edits[${index}] is not an operation object.` };
    }
    const { op, path, value, index: insertIndex } = raw as Record<string, unknown>;
    if (typeof op !== 'string' || !VALID_EDIT_OPS.has(op)) {
      return { error: `edits[${index}]: unknown op "${op}". Use "replace", "insert", "delete" or "test".` };
    }
    if (typeof path !== 'string' || path === '') return { error: `edits[${index}]: a "path" is required.` };
    if ((op === 'replace' || op === 'insert' || op === 'test') && value === undefined) {
      return { error: `edits[${index}]: "value" is required for ${op}.` };
    }
    ops.push({ op, path, value, index: insertIndex } as ConfigEditOp);
  }

  const markers = omittedMarkerPaths(parsed);
  if (markers.length > 0) {
    const readTool = opts?.readToolName ?? 'getObject';
    return {
      error: `The edits still contain the placeholder for omitted translations at: ${markers.join(', ')}. `
        + `${readTool} hides non-English translations unless you pass showAllLangs: true. `
        + 'Re-read the object with showAllLangs: true and write the real translations back, '
        + 'or target only the specific language you mean to change.',
    };
  }
  return { ops };
}

export function formatDistroIssue(result: ValidationResult): string {
  return `[${result.severity}] ${result.name} at ${result.breadcrumbs.join(' > ')}`
    + (result.result.message ? `: ${result.result.message}` : '');
}

export function formatDistroIssues(
  results: ValidationResult[],
  limit = 100,
): { issues: string[]; totalCount: number } {
  const issues = results.slice(0, limit).map(formatDistroIssue);
  if (results.length > limit) {
    issues.push(`…and ${results.length - limit} more (${results.length} total). Fix some and run this again for the rest.`);
  }
  return { issues, totalCount: results.length };
}
